/** @jsxImportSource @emotion/react */
import { ComponentProps, FC, useEffect } from 'react'
import { Form } from 'react-final-form'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import { FormHelperText } from '@mui/material'
import { BaseModal } from 'components/Modals/BaseModal'
import { SelectField } from 'components/Select'
import { DentalSchoolsEnum, FacultyModeCohortYearEnum, PlatformName } from '__generatedTypes__/globalTypes'
import { useFacultySchools } from 'pagesNew/Users/Edit/components/hooks'
import { COHORT_ITEMS, renderSchoolItem } from 'pagesNew/Users/Edit/components/FacultyFields/FacultyFields'
import { FIND_ALL_INSTITUTIONAL_CODES } from 'queries/institutionalCodes'
import { toast } from 'utils/toast'
import { validateRequired } from 'utils/validation'
import { useCreateInstitutionalCodesMutation } from '../hooks'
import { extractErrorMessage } from '../helpers'
import * as classes from './styles'

type Props = {
  open: boolean
  onClose: () => void
  platformName: PlatformName | undefined
  selectedPlatform?: { id: string } | null
}

type FormValues = {
  university: { id: DentalSchoolsEnum; title: string }
  cohort: { id: FacultyModeCohortYearEnum; title: string }
}

type OnSubmitHandler = ComponentProps<typeof Form<FormValues>>['onSubmit']

export const AddPartnershipModal: FC<Props> = ({ open, onClose, platformName, selectedPlatform }) => {
  const { schools, fetchSchools, loading: schoolsLoading } = useFacultySchools()
  const [createInstitutionalCodes] = useCreateInstitutionalCodesMutation()

  const handleSubmit: OnSubmitHandler = async (values) => {
    try {
      await createInstitutionalCodes({
        variables: {
          createInstitutionalCodesInput: {
            cohort: values.cohort.id,
            platform: selectedPlatform?.id ? { id: selectedPlatform.id } : null,
            university: values.university.id,
          },
        },
        awaitRefetchQueries: true,
        refetchQueries: [FIND_ALL_INSTITUTIONAL_CODES],
      })
      toast.success('Partnership created successfully!')
      onClose()
    } catch (error) {
      console.error('Error submitting partnership:', error)
      return { root: extractErrorMessage(error) }
    }
  }

  useEffect(() => {
    if (open && !schools?.length && platformName) {
      fetchSchools(platformName)
    }
  }, [open, schools?.length, fetchSchools, platformName])

  return (
    <BaseModal open={open} onClose={onClose}>
      <div css={classes.headerSection}>
        <Typography css={classes.title} variant="title2">
          New Institution
        </Typography>
      </div>

      <Form<FormValues>
        onSubmit={handleSubmit}
        render={({ handleSubmit, submitting, submitErrors }) => (
          <form onSubmit={handleSubmit} css={classes.form}>
            <div css={classes.fieldSection}>
              <SelectField
                validate={validateRequired}
                name="university"
                label="University"
                items={schools || []}
                renderItem={renderSchoolItem}
                placeholder="School"
                fullWidth
                loading={schoolsLoading}
              />
            </div>

            <div css={classes.fieldSection}>
              <SelectField
                name="cohort"
                label="Cohort"
                validate={validateRequired}
                items={COHORT_ITEMS}
                renderItem={renderSchoolItem}
                placeholder="Cohort"
                fullWidth
              />

              {submitErrors?.root && <FormHelperText error>{submitErrors.root}</FormHelperText>}

              <Button
                type="submit"
                variant="contained"
                color="blue"
                size="large"
                fullWidth
                disabled={submitting}
                css={classes.submitButton}
              >
                {submitting ? <CircularProgress size={16} color="inherit" /> : 'Submit'}
              </Button>
            </div>
          </form>
        )}
      />
    </BaseModal>
  )
}
